import type { EditorShellState, EntityPreview } from "@dxf-ai-editor/contracts";
import type {
  DrawingDocument,
  DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

/**
 * Track 1 / Phase 1 — explicit reducer-mutation contract.
 *
 * Reducers will be migrated (Phase 3b) to return `ReducerMutationResult`
 * so the dispatch layer can apply incremental cache + index updates in
 * O(|touched|) instead of inferring diffs from a freshly-validated
 * document. This file owns the contract types and the canonical
 * `NO_MUTATIONS` sentinel; consumers (mutationStore, commitMutation,
 * future reducer migrations) import from here.
 *
 * Phase 1 introduces these in parallel — no producer/consumer wired up.
 * Track-1 plan: docs/plans/2026-05-06_state_layer_unification.md (v4).
 *
 * Phase 3b PR #1 — `EntityMutation` parameterised over the entity
 * payload type. Geometry-side reducers carry `DrawingEntity` (default);
 * shell-side reducers (propertyReducer / shellUiReducer) carry
 * `EntityPreview` because `state.drawing.entities` holds previews, not
 * full geometry. Both share the same `removed: { spaceId, entityId }`
 * shape — entity payload only matters for `added`/`updated`. Generic
 * default keeps existing callsites (`EntityMutation`, `NO_MUTATIONS`)
 * source-compatible.
 */

export type StyleTableTouchKind =
  | "layers"
  | "dimensionStyles"
  | "multileaderStyles"
  | "textStyles"
  // PR-LT2 (plan §10.7 / Codex#1 F13) — a linetype delete/rename mutation
  // touches the LTYPE table; the dependency index (PR2) keys on this so a
  // future Linetype Manager (PR-LT5) can keep entity.linetype refs current.
  | "linetypes";

export interface EntityMutation<E = DrawingEntity> {
  added: ReadonlyArray<{ spaceId: string; entity: E }>;
  removed: ReadonlyArray<{ spaceId: string; entityId: string }>;
  updated: ReadonlyArray<{
    spaceId: string;
    prev: E;
    next: E;
  }>;
  styleTablesTouched: ReadonlySet<StyleTableTouchKind>;
}

export interface ReducerMutationResult {
  document: DrawingDocument;
  mutations: EntityMutation;
}

/**
 * Shell-side reducer mutation result — returned by reducers that
 * operate on `EditorShellState` (propertyReducer, shellUiReducer).
 * `state` is non-null because callers route `null` (command did not
 * match) BEFORE wrapping in this shape; see `wrapReducerLegacy`.
 *
 * `mutations` carries `EntityPreview` payloads because the shell side
 * only sees previews — actual `DrawingEntity` mutations land via the
 * geometry reducer (separate migration mini-PRs #2-5).
 */
export interface ShellReducerMutationResult {
  state: EditorShellState;
  mutations: EntityMutation<EntityPreview>;
}

/**
 * Frozen sentinel for the "no entity-side change" case. Reducers that
 * touch only style tables (layers / dim styles / ...) or that early-out
 * on a no-op command can return this verbatim — `commitMutation`
 * fast-paths it into a true no-op.
 *
 * Frozen so accidental mutation surfaces immediately in dev. Typed at
 * the most-abstract `unknown` payload so it satisfies any concrete
 * `EntityMutation<E>` instantiation; runtime arrays are empty so the
 * cast is safe.
 */
const _NO_MUTATIONS_FROZEN: EntityMutation<unknown> = Object.freeze({
  added: Object.freeze([]) as ReadonlyArray<{
    spaceId: string;
    entity: unknown;
  }>,
  removed: Object.freeze([]) as ReadonlyArray<{
    spaceId: string;
    entityId: string;
  }>,
  updated: Object.freeze([]) as ReadonlyArray<{
    spaceId: string;
    prev: unknown;
    next: unknown;
  }>,
  styleTablesTouched: Object.freeze(
    new Set<StyleTableTouchKind>(),
  ) as ReadonlySet<StyleTableTouchKind>,
});

export const NO_MUTATIONS: EntityMutation =
  _NO_MUTATIONS_FROZEN as EntityMutation;

/**
 * Phase 3b PR #1 — typed accessor for shell-side `EntityPreview`
 * payloads. Returns the same frozen sentinel object retyped to the
 * shell payload type so every callsite can use it without crafting a
 * fresh empty record.
 */
export const NO_SHELL_MUTATIONS: EntityMutation<EntityPreview> =
  _NO_MUTATIONS_FROZEN as EntityMutation<EntityPreview>;

/**
 * Transitional adapter for shell-side reducers that haven't been
 * migrated to the `ShellReducerMutationResult` contract. Wraps the
 * legacy `(state) => state` return into `{ state, mutations: NO_SHELL_MUTATIONS }`.
 * `commitMutation` receiving `NO_MUTATIONS` performs no incremental
 * updates — caller must rely on the legacy allowlist resync until the
 * reducer is migrated (or until `needsShellResyncFromGeometry` is
 * removed in Phase 3b PR #6).
 *
 * Remove this helper once all reducers return `ShellReducerMutationResult`
 * (Phase 3b complete).
 */
export function wrapReducerLegacy(
  state: EditorShellState,
): ShellReducerMutationResult {
  return { state, mutations: NO_SHELL_MUTATIONS };
}
